import React, { useState, useEffect } from "react";
import {
  Upload,
  FileSpreadsheet,
  FileDown,
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  CheckCircle,
  XCircle,
  Download,
  Ship,
  Anchor,
  Calendar,
  DollarSign,
  AlertTriangle,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { useNotificaciones } from "../contexts/NotificacionesContext";
import ModalReporteCliente from "../components/ModalReporteCliente";
import TimelineEstados from "../components/TimeLineEstados";
import ContenedorDetailPanel from "../components/ContenedorDetailPanel";
import FolioCombobox from "../components/FolioCombobox";
import ConfirmModal from "../components/ConfirmModal";
import apiService from "../services/api";

const MS_POR_DIA = 1000 * 60 * 60 * 24;

const parseLocalDate = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const formatLocalDate = (date) => {
  if (!date) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const calcularEstadoLocal = (ultimoDiaSinDemoras, fechaVacio) => {
  const fechaLimite = parseLocalDate(ultimoDiaSinDemoras);

  if (!fechaLimite) {
    return { diasRestantes: null, diasDemora: 0, urgencia: null };
  }

  // Si hay fecha vacío, usar esa fecha para congelar los cálculos
  // Si no hay fecha vacío, usar la fecha actual
  const fechaReferencia = parseLocalDate(fechaVacio) || startOfToday();
  const diffDays = Math.floor(
    (fechaLimite.getTime() - fechaReferencia.getTime()) / MS_POR_DIA
  );
  const diasRestantes = diffDays;

  // Los costos SOLO se calculan cuando ya pasó el último día (diasRestantes < 0)
  const diasDemora = diasRestantes < 0 ? Math.abs(diasRestantes) : 0;

  let urgencia = null;

  // Lógica de alertas (alineada con el backend):
  // - 4-5 días restantes → VERDE (baja) - alerta preventiva
  // - 2-3 días restantes → AMARILLO (media) - alerta
  // - 0-1 días restantes → ROJO (alta) - último día sin demoras
  // - < 0 días → ROJO CRÍTICO (critica) - demoras activas
  // - 6+ días → sin alerta

  if (diasRestantes >= 4 && diasRestantes <= 5) {
    urgencia = "baja"; // 4-5 días → VERDE
  } else if (diasRestantes >= 2 && diasRestantes <= 3) {
    urgencia = "media"; // 2-3 días → AMARILLO
  } else if (diasRestantes >= 0 && diasRestantes <= 1) {
    urgencia = "alta"; // 0-1 días → ROJO (último día sin demoras)
  } else if (diasRestantes < 0) {
    urgencia = "critica"; // < 0 → ROJO CRÍTICO (demoras activas)
  } else {
    urgencia = null; // 6+ días → sin alerta
  }

  return { diasRestantes, diasDemora, urgencia };
};

const construirAlertaUrgencia = (diasRestantes, diasDemora, urgencia) => {
  if (diasRestantes === null || diasRestantes === undefined || diasRestantes > 5) {
    return null;
  }

  const estilos = {
    baja: { bgColor: "#DCFCE7", textColor: "#166534" },
    media: { bgColor: "#FFEDD5", textColor: "#9A3412" },
    alta: { bgColor: "#FEE2E2", textColor: "#991B1B" },
    critica: { bgColor: "#991B1B", textColor: "#FFFFFF" },
  };

  let mensaje = "";

  if (diasRestantes >= 2) {
    mensaje = `Quedan ${diasRestantes} dias sin demoras`;
  } else if (diasRestantes === 1) {
    mensaje = "Mañana inician las demoras";
  } else if (diasRestantes === 0) {
    mensaje = "Hoy es el ultimo dia sin demoras";
  } else {
    mensaje = `Cálculo de Demoras al día de hoy:  ${diasDemora} dia${diasDemora === 1 ? "" : "s"}`;
  }

  return {
    nivel: urgencia,
    mensaje,
    ...estilos[urgencia],
  };
};

const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 4000;
    const interval = 10;
    const decrement = (100 * interval) / duration;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return prev - decrement;
      });
    }, interval);

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  const config = {
    success: {
      icon: <CheckCircle size={22} />,
      bgColor: "bg-white border border-green-200 shadow-soft",
      progressColor: "bg-green-500",
      textColor: "text-green-800",
    },
    error: {
      icon: <XCircle size={22} />,
      bgColor: "bg-white border border-red-200 shadow-soft",
      progressColor: "bg-red-500",
      textColor: "text-red-800",
    },
  };

  const currentConfig = config[type] || config.success;

  return (
    <div
      className={`${currentConfig.bgColor} ${currentConfig.textColor} rounded-xl overflow-hidden min-w-[320px] max-w-md animate-slideIn`}
    >
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{currentConfig.icon}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-relaxed break-words">
            {message}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 hover:bg-ink-100 rounded-lg p-1.5 transition-all duration-200 ml-2"
        >
          <X size={18} />
        </button>
      </div>
      <div className="h-1 bg-ink-100">
        <div
          className={`h-full ${currentConfig.progressColor} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-3">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lifted max-w-md w-full animate-slideIn">
        <div className="px-6 py-4 border-b border-ink-200">
          <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-ink-600">{message}</p>
        </div>
        <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

function Contenedores({ contenedorBuscado, onContenedorVisto }) {
  const { refrescar } = useNotificaciones();

  const [registros, setRegistros] = useState([]);
  const [filteredRegistros, setFilteredRegistros] = useState([]);
  const [navieras, setNavieras] = useState([]);
  const [puertos, setPuertos] = useState([]);
  const [tarifasDisponibles, setTarifasDisponibles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [toasts, setToasts] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [modalReporteAbierto, setModalReporteAbierto] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [recalculando, setRecalculando] = useState(false);
  const [foliosDisponibles, setFoliosDisponibles] = useState([]);
  const [modoFolio, setModoFolio] = useState("nuevo"); // "nuevo" o "existente"
  const [folioSeleccionado, setFolioSeleccionado] = useState("");
  const [folioConfirmModal, setFolioConfirmModal] = useState({
    isOpen: false,
    folioValue: '',
    datosUltimoRegistro: null
  });

  const [formData, setFormData] = useState({
    folio: "",
    master: "",
    contenedor: "",
    cliente: "",
    naviera: "",
    pod: "",
    tarifaId: "",
    ata: "",  // ⬅️ CAMBIO: ETA → ATA
    ultimoDiaSinDemoras: "",
    diasIncurridos: "",
    fechaVacio: "",
    costoNavieraSinIVA: "",
    ventaClienteSinIVA: "",
    ultimaFactura: "",
    factura: "NO",
    hastaQueFecha: "",
    facturaPagoNaviera: "",
    pagado: "NO",
    aplicaIVA: true,
    aplicaMargen: true,
  });

  const [tarifaPorDia, setTarifaPorDia] = useState(0);
  const [tarifaIncluyeIVA, setTarifaIncluyeIVA] = useState(false);
  const [diasDemoras, setDiasDemoras] = useState(0);
  const [diasAFacturar, setDiasAFacturar] = useState(0);
  const [alertaUrgencia, setAlertaUrgencia] = useState(null);
  // Campos exclusivos para MAERSK — todos arrancan vacíos, el usuario los llena
  const [tarifaMaerskManual, setTarifaMaerskManual] = useState('');   // Tarifa tier 1
  const [diasLibresMaersk, setDiasLibresMaersk] = useState('');       // Días libres
  const [maerskTier1Dias, setMaerskTier1Dias] = useState('');         // Días del tier 1 (vacío = tarifa plana)
  const [maerskTier2Tarifa, setMaerskTier2Tarifa] = useState('');     // Tarifa tier 2 (vacío = sin tier 2)

  const [calculatedValues, setCalculatedValues] = useState({
    costoNavieraConIVA: 0,
    ventaClienteConIVA: 0,
    utilidad: 0,
  });

  const [calculatedFacturacion, setCalculatedFacturacion] = useState({
    ventaFacturacionSinIVA: 0,
    ventaFacturacionConIVA: 0,
  });

  const [mostrarTimeline, setMostrarTimeline] = useState(false);
  const [contenedorTimeline, setContenedorTimeline] = useState(null);

  // Estados para panel lateral
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);

  // Estados para modal de días libres
  const [showDiasLibresModal, setShowDiasLibresModal] = useState(false);
  // `diasLibresOverride` = null => usar días de la naviera (o 21 por defecto)
  // cualquier número (incluye 21) => selección manual confirmada
  const [diasLibresOverride, setDiasLibresOverride] = useState(null);
  // Valor editable dentro del modal (no aplica hasta "Confirmar")
  const [diasLibresModalValue, setDiasLibresModalValue] = useState(21);

  const obtenerDiasLibresSeleccionados = () => {
    // Si el usuario ya seleccionó manualmente, usar ese valor
    if (diasLibresOverride !== null) return diasLibresOverride;

    // Si no, usar los de la naviera
    const navieraSeleccionada = navieras.find(
      (naviera) => naviera.nombre === formData.naviera
    );

    if (!navieraSeleccionada || navieraSeleccionada.dias_libres === undefined) {
      return 21;
    }

    const diasLibres = parseInt(navieraSeleccionada.dias_libres, 10);
    return Number.isNaN(diasLibres) ? 21 : diasLibres;
  };

  // Función para manejar cambio de ATA
  const handleAtaChange = (ataValue) => {
    setFormData((prev) => ({ ...prev, ata: ataValue }));

    if (ataValue) {
      // Para MAERSK usa días libres manuales; para otras navieras el valor de la naviera
      const diasLibresMaerskNum = parseInt(diasLibresMaersk);
      const diasLibres = esMaersk(formData.naviera)
        ? (!isNaN(diasLibresMaerskNum) && diasLibresMaerskNum > 0 ? diasLibresMaerskNum : null)
        : obtenerDiasLibresSeleccionados();
      if (esMaersk(formData.naviera) && !diasLibres) return; // esperar a que el usuario llene días libres
      recalcularDesdeFechas(
        ataValue,
        formData.fechaVacio,
        diasLibres
      );
    } else {
      // Limpiar el campo si se borra ATA
      setFormData((prev) => ({
        ...prev,
        ultimoDiaSinDemoras: "",
        diasIncurridos: "",
        costoNavieraSinIVA: "",
        ventaClienteSinIVA: "",
      }));
      setAlertaUrgencia(null);
    }
  };

  // Función para confirmar días libres seleccionados (Solo si se abre el modal manual)
  const confirmarDiasLibres = (dias) => {
    setDiasLibresOverride(dias);
    setShowDiasLibresModal(false);

    // Recalcular con los días seleccionados
    // Usar formData.ata directamente para mayor robustez
    recalcularDesdeFechas(formData.ata, formData.fechaVacio, dias);
  };

  const resetForm = () => {
    setFormData({
      folio: "",
      master: "",
      contenedor: "",
      cliente: "",
      naviera: "",
      tarifaId: "",
      ata: "",
      ultimoDiaSinDemoras: "",
      diasIncurridos: "",
      fechaVacio: "",
      costoNavieraSinIVA: "",
      ventaClienteSinIVA: "",
      ultimaFactura: "",
      factura: "NO",
      hastaQueFecha: "",
      facturaPagoNaviera: "",
      aplicaIVA: true,
      aplicaMargen: true,
    });
    setTarifaPorDia(0);
    setTarifaIncluyeIVA(false);
    setTarifasDisponibles([]);
    setDiasDemoras(0);
    setDiasAFacturar(0);
    setAlertaUrgencia(null);
    setDiasLibresOverride(null);
    setDiasLibresModalValue(21);
    setTarifaMaerskManual('');
    setDiasLibresMaersk('');
    setMaerskTier1Dias('');
    setMaerskTier2Tarifa('');
    setCalculatedValues({
      costoNavieraConIVA: 0,
      ventaClienteConIVA: 0,
      utilidad: 0,
    });
    setCalculatedFacturacion({
      ventaFacturacionSinIVA: 0,
      ventaFacturacionConIVA: 0,
    });
  };

  const resetFormKeepingFolio = (folioToKeep) => {
    setFormData({
      folio: folioToKeep,  // ← MANTENER el folio
      master: "",
      contenedor: "",
      cliente: "",
      naviera: "",
      pod: "",
      tarifaId: "",
      ata: "",
      ultimoDiaSinDemoras: "",
      diasIncurridos: "",
      fechaVacio: "",
      costoNavieraSinIVA: "",
      ventaClienteSinIVA: "",
      ultimaFactura: "",
      factura: "NO",
      hastaQueFecha: "",
      facturaPagoNaviera: "",
      aplicaIVA: true,
      aplicaMargen: true,
    });

    // Resetear todos los estados relacionados
    setTarifaPorDia(0);
    setTarifaIncluyeIVA(false);
    setTarifasDisponibles([]);
    setDiasDemoras(0);
    setDiasAFacturar(0);
    setAlertaUrgencia(null);
    setDiasLibresOverride(null);
    setDiasLibresModalValue(21);
    setTarifaMaerskManual('');
    setDiasLibresMaersk('');
    setMaerskTier1Dias('');
    setMaerskTier2Tarifa('');
    setCalculatedValues({
      costoNavieraConIVA: 0,
      ventaClienteConIVA: 0,
      utilidad: 0,
    });
    setCalculatedFacturacion({
      ventaFacturacionSinIVA: 0,
      ventaFacturacionConIVA: 0,
    });
  };

  const aplicarDatosAutocompletado = async (datosUltimoRegistro, value) => {
    // Autocompletar formulario EXCEPTO el campo contenedor
    setFormData({
      folio: datosUltimoRegistro.folio || "",
      master: datosUltimoRegistro.master || "",
      contenedor: "",  // ← Dejar vacío para nuevo contenedor
      cliente: datosUltimoRegistro.cliente || "",
      naviera: datosUltimoRegistro.naviera || "",
      pod: datosUltimoRegistro.pod || "",
      tarifaId: datosUltimoRegistro.tarifaId || "",
      ata: datosUltimoRegistro.ata || "",
      ultimoDiaSinDemoras: datosUltimoRegistro.ultimoDiaSinDemoras || "",
      diasIncurridos: datosUltimoRegistro.diasIncurridos || "",
      fechaVacio: datosUltimoRegistro.fechaVacio || "",
      costoNavieraSinIVA: datosUltimoRegistro.costoNavieraSinIVA || "",
      ventaClienteSinIVA: datosUltimoRegistro.ventaClienteSinIVA || "",
      ultimaFactura: datosUltimoRegistro.ultimaFactura || "",
      factura: datosUltimoRegistro.factura || "NO",
      hastaQueFecha: datosUltimoRegistro.hastaQueFecha || "",
      facturaPagoNaviera: datosUltimoRegistro.facturaPagoNaviera || "",
      pagado: "NO",
      // Restaurar toggles exactamente como estaban en el registro anterior
      aplicaIVA: datosUltimoRegistro.aplicaIVA !== undefined && datosUltimoRegistro.aplicaIVA !== null
        ? Boolean(datosUltimoRegistro.aplicaIVA) : true,
      aplicaMargen: datosUltimoRegistro.aplicaMargen !== undefined && datosUltimoRegistro.aplicaMargen !== null
        ? Boolean(datosUltimoRegistro.aplicaMargen) : true,
    });

    // ✅ COPIAR DIRECTAMENTE todos los valores calculados que vienen del backend
    setDiasDemoras(parseFloat(datosUltimoRegistro.diasDemoras) || 0);
    setDiasAFacturar(parseFloat(datosUltimoRegistro.diasAFacturar) || 0);

    // Copiar valores calculados de costos y ventas directamente desde el backend
    const costoConIVA = parseFloat(datosUltimoRegistro.costoNavieraConIVA) || 0;
    const ventaConIVA = parseFloat(datosUltimoRegistro.ventaClienteConIVA) || 0;
    const utilidad = ventaConIVA - costoConIVA;

    setCalculatedValues({
      costoNavieraConIVA: costoConIVA,
      ventaClienteConIVA: ventaConIVA,
      utilidad: utilidad,
    });

    // Copiar valores de facturación directamente desde el backend
    setCalculatedFacturacion({
      ventaFacturacionSinIVA: parseFloat(datosUltimoRegistro.ventaFacturacionSinIVA) || 0,
      ventaFacturacionConIVA: parseFloat(datosUltimoRegistro.ventaFacturacionConIVA) || 0,
    });

    // Restaurar campos Maersk si el registro anterior era de esa naviera
    if (esMaersk(datosUltimoRegistro.naviera)) {
      const diasRec = calcularDiasLibresDesdeRegistro(datosUltimoRegistro.ata, datosUltimoRegistro.ultimoDiaSinDemoras);
      setDiasLibresMaersk(diasRec ? String(diasRec) : '');
      setTarifaMaerskManual(datosUltimoRegistro.tarifa_override != null
        ? String(parseFloat(datosUltimoRegistro.tarifa_override)) : '');
      setMaerskTier1Dias(datosUltimoRegistro.maersk_tier1_dias != null
        ? String(datosUltimoRegistro.maersk_tier1_dias) : '');
      setMaerskTier2Tarifa(datosUltimoRegistro.maersk_tier2_tarifa != null
        ? String(parseFloat(datosUltimoRegistro.maersk_tier2_tarifa)) : '');
      if (datosUltimoRegistro.tarifa_override != null) {
        const tarifaEfectiva = calcularTarifaEfectivaMaersk(
          parseFloat(datosUltimoRegistro.diasDemoras) || 0,
          datosUltimoRegistro.maersk_tier1_dias,
          datosUltimoRegistro.tarifa_override,
          datosUltimoRegistro.maersk_tier2_tarifa
        );
        setTarifaPorDia(tarifaEfectiva);
      }
    } else if (datosUltimoRegistro.naviera) {
      // Naviera normal: cargar tarifas desde la API
      const navieraSeleccionada = navieras.find((n) => n.nombre === datosUltimoRegistro.naviera);

      if (navieraSeleccionada) {
        try {
          const tarifas = await apiService.get(`/tarifas/naviera/${navieraSeleccionada.id}`);

          if (tarifas && tarifas.length > 0) {
            setTarifasDisponibles(tarifas);

            const tarifaUsada = tarifas.find((t) => t.id === datosUltimoRegistro.tarifaId);
            const tarifaAUsar = tarifaUsada || tarifas[0];

            const tarifaDia = parseFloat(tarifaAUsar.tarifaSinIVA);
            setTarifaPorDia(tarifaDia);
            setTarifaIncluyeIVA(tarifaAUsar.incluyeIVA || false);
          }
        } catch (error) {
          if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
            showToast("Error al cargar tarifas", "error");
          }
        }
      }
    }

    // Calcular alerta de urgencia basada en el último día sin demoras
    if (datosUltimoRegistro.ultimoDiaSinDemoras) {
      const estado = calcularEstadoLocal(
        datosUltimoRegistro.ultimoDiaSinDemoras,
        datosUltimoRegistro.fechaVacio || ""
      );

      const alerta = construirAlertaUrgencia(
        estado.diasRestantes,
        estado.diasDemora,
        estado.urgencia
      );
      setAlertaUrgencia(alerta);
    } else {
      setAlertaUrgencia(null);
    }

    showToast(`Datos cargados del folio ${value}. Ingresa el nuevo contenedor.`, "success");
  };

  const fetchRegistros = async () => {
    try {
      const data = await apiService.get('/registros');
      setRegistros(data);
      setFilteredRegistros(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al cargar registros", "error");
      }
    }
  };

  const fetchNavieras = async () => {
    try {
      const data = await apiService.get('/navieras');
      setNavieras(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al cargar navieras", "error");
      }
    }
  };

  const fetchPuertos = async () => {
    try {
      const data = await apiService.get('/puertos');
      setPuertos(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al cargar puertos", "error");
      }
    }
  };

  const fetchFolios = async () => {
    try {
      const data = await apiService.get('/folios');
      setFoliosDisponibles(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al cargar folios", "error");
      }
    }
  };

  const fetchClientes = async () => {
    try {
      const data = await apiService.get('/clientes');
      setClientes(data);
    } catch (error) {
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al cargar clientes", "error");
      }
    }
  };

  const handleFolioSelectionChange = async (value) => {
    setModoFolio(value);
    setFolioSeleccionado(value === "nuevo" ? "" : value);

    if (value === "nuevo") {
      resetForm();
      return;
    }

    if (value && value !== "nuevo") {
      try {
        const datosUltimoRegistro = await apiService.get(
          `/folios/${encodeURIComponent(value)}/ultimo-registro`
        );

        // ✅ NUEVO: Mostrar modal de confirmación
        setFolioConfirmModal({
          isOpen: true,
          folioValue: value,
          datosUltimoRegistro: datosUltimoRegistro
        });

      } catch (error) {
        if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
          showToast("Error al cargar datos del folio", "error");
        }
        resetForm();
      }
    }
  };

  // ✅ NUEVO: Handler para seleccionar folio existente desde Combobox
  const handleFolioSelect = async (folioValue) => {
    // Si el valor está vacío, resetear formulario
    if (!folioValue || folioValue === '') {
      setModoFolio("nuevo");
      setFolioSeleccionado("");
      setFormData((prev) => ({
        ...prev,
        folio: "",
      }));
      return;
    }

    // Mismo comportamiento que seleccionar un folio existente
    await handleFolioSelectionChange(folioValue);
  };

  // ✅ NUEVO: Handler para crear nuevo folio desde Combobox
  const handleCreateNewFolio = (folioText) => {
    // Establecer modo "nuevo" y el texto del folio
    setModoFolio("nuevo");
    setFolioSeleccionado("");
    setFormData((prev) => ({
      ...prev,
      folio: folioText,
    }));
    showToast(`Listo para crear nuevo folio: ${folioText}`, "success");
  };

  const handleCancelarModalFolio = () => {
    const { folioValue } = folioConfirmModal;

    resetFormKeepingFolio(folioValue);
    setModoFolio("nuevo");
    setFolioSeleccionado("");

    setFolioConfirmModal({
      isOpen: false,
      folioValue: '',
      datosUltimoRegistro: null
    });

    showToast(`Formulario limpio para folio: ${folioValue}. Listo para ingresar nuevo contenedor.`, "info");
  };

  const handleConfirmarCargaDatos = async () => {
    const { folioValue, datosUltimoRegistro } = folioConfirmModal;

    await aplicarDatosAutocompletado(datosUltimoRegistro, folioValue);

    setFolioConfirmModal({
      isOpen: false,
      folioValue: '',
      datosUltimoRegistro: null
    });
  };

  const abrirModalReporte = async () => {
    setModalReporteAbierto(true);
    await fetchClientes();
  };

  // ✅ FUNCIÓN: Recalcular costos de todos los registros
  const recalcularCostos = async () => {
    if (!window.confirm('¿Recalcular costos de TODOS los registros? Esto actualizará las demoras actuales.')) {
      return;
    }

    setRecalculando(true);

    try {
      const data = await apiService.post('/registros/recalcular-costos', {});

      if (data.success) {
        showToast(`✅ ${data.actualizados} registros actualizados, ${data.sinCambios} sin cambios`, 'success');
        await fetchRegistros();

        // Refrescar notificaciones
        if (typeof refrescar === 'function') {
          refrescar();
        }
      } else {
        showToast('Error al recalcular costos', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast('Error de conexión al recalcular', 'error');
      }
    } finally {
      setRecalculando(false);
    }
  };

  useEffect(() => {
    fetchRegistros();
    fetchNavieras();
    fetchPuertos();
    fetchClientes();
    fetchFolios();
  }, []);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setFilteredRegistros(registros);
      setCurrentPage(1);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    // Primero, verificar si hay folios que coincidan con el término de búsqueda
    const foliosCoincidentes = new Set();
    registros.forEach((registro) => {
      if (registro.folio && registro.folio.toLowerCase().includes(searchLower)) {
        foliosCoincidentes.add(registro.folio);
      }
    });

    // Si encontramos folios coincidentes, traer TODOS los contenedores con esos folios
    if (foliosCoincidentes.size > 0) {
      const filtered = registros.filter((registro) =>
        foliosCoincidentes.has(registro.folio)
      );
      setFilteredRegistros(filtered);
      setCurrentPage(1);
      return;
    }

    // Si no hay coincidencias de folio, hacer búsqueda normal en todos los campos
    const filtered = registros.filter((registro) =>
      Object.values(registro).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchLower)
      )
    );
    setFilteredRegistros(filtered);
    setCurrentPage(1);
  }, [searchTerm, registros]);

  // Efecto para navegar a un contenedor desde notificaciones
  useEffect(() => {
    if (contenedorBuscado && registros.length > 0) {
      setSearchTerm(contenedorBuscado);
      // Buscar el registro exacto para abrir el panel lateral
      const registro = registros.find(
        (r) => r.contenedor && r.contenedor.toLowerCase() === contenedorBuscado.toLowerCase()
      );
      if (registro) {
        // Usar un pequeño delay para que el filtro se aplique primero
        setTimeout(() => {
          abrirPanel(registro);
        }, 100);
      }
      // Limpiar el estado para que no se repita si el usuario vuelve a la vista
      if (onContenedorVisto) {
        onContenedorVisto();
      }
    }
  }, [contenedorBuscado, registros]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortedRegistros = React.useMemo(() => {
    if (!sortColumn) return filteredRegistros;

    return [...filteredRegistros].sort((a, b) => {
      let valA, valB;

      switch (sortColumn) {
        case "estadoDemora":
          valA = a.diasRestantes ?? -9999;
          valB = b.diasRestantes ?? -9999;
          break;
        case "contenedor":
          valA = (a.contenedor || "").toLowerCase();
          valB = (b.contenedor || "").toLowerCase();
          break;
        case "cliente":
          valA = (a.cliente || "").toLowerCase();
          valB = (b.cliente || "").toLowerCase();
          break;
        case "naviera":
          valA = (a.naviera || "").toLowerCase();
          valB = (b.naviera || "").toLowerCase();
          break;
        case "diasDemoras":
          valA = parseFloat(a.diasDemoras) || 0;
          valB = parseFloat(b.diasDemoras) || 0;
          break;
        case "costoNavieraConIVA":
          valA = parseFloat(a.costoNavieraConIVA) || 0;
          valB = parseFloat(b.costoNavieraConIVA) || 0;
          break;
        case "ventaClienteConIVA":
          valA = parseFloat(a.ventaClienteConIVA) || 0;
          valB = parseFloat(b.ventaClienteConIVA) || 0;
          break;
        default:
          return 0;
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRegistros, sortColumn, sortDirection]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedRegistros.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(sortedRegistros.length / itemsPerPage);

  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const esMaersk = (nombreNaviera) =>
    typeof nombreNaviera === 'string' && nombreNaviera.toUpperCase().includes('MAERSK');

  // Recupera los días libres que se usaron en un registro a partir de ATA y ultimoDiaSinDemoras
  const calcularDiasLibresDesdeRegistro = (ata, ultimoDia) => {
    if (!ata || !ultimoDia) return null;
    const fechaAta = parseLocalDate(ata);
    const fechaUltimo = parseLocalDate(ultimoDia);
    if (!fechaAta || !fechaUltimo) return null;
    const diff = Math.floor((fechaUltimo.getTime() - fechaAta.getTime()) / MS_POR_DIA);
    return diff >= 0 ? diff + 1 : null;
  };

  const handleNavieraChange = async (navieraValue) => {
    // Evita arrastrar selección manual entre navieras
    setDiasLibresOverride(null);
    setFormData((prev) => ({ ...prev, naviera: navieraValue, tarifaId: "" }));
    setTarifasDisponibles([]);
    setTarifaPorDia(0);

    const navieraSeleccionada = navieras.find((n) => n.nombre === navieraValue);
    const diasLibres = navieraSeleccionada?.dias_libres
      ? parseInt(navieraSeleccionada.dias_libres, 10)
      : 21;

    // Modo MAERSK: todos los campos manuales — el usuario los llena según la plataforma de Maersk
    if (esMaersk(navieraValue)) {
      // Pre-llenar solo los días libres desde la configuración de la naviera (dato base)
      // Las tarifas y tramos los escribe el usuario consultando la plataforma de Maersk
      setDiasLibresMaersk('');
      setTarifaMaerskManual('');
      setMaerskTier1Dias('');
      setMaerskTier2Tarifa('');
      setTarifaPorDia(0);
      return;
    }

    // Flujo normal para todas las demás navieras
    setTarifaMaerskManual('');

    if (navieraSeleccionada) {
      try {
        const tarifas = await apiService.get(`/tarifas/naviera/${navieraSeleccionada.id}`);

        if (tarifas && tarifas.length > 0) {
          setTarifasDisponibles(tarifas);
          const tarifaDefault = tarifas.find((t) => t.esDefault);
          const tarifaAUsar = tarifaDefault || tarifas[0];

          setFormData((prev) => ({
            ...prev,
            tarifaId: tarifaAUsar.id,
          }));

          const tarifaDia = parseFloat(tarifaAUsar.tarifaSinIVA);
          setTarifaPorDia(tarifaDia);
          setTarifaIncluyeIVA(tarifaAUsar.incluyeIVA || false);

          if (formData.ata) {
            recalcularDesdeFechas(formData.ata, formData.fechaVacio, diasLibres);
          } else {
            const diasDemo = parseFloat(formData.diasIncurridos) || 0;
            actualizarCalculos({ diasDemora: diasDemo }, tarifaDia);
          }

          if (diasAFacturar > 0) {
            calcularVentaFacturacion(diasAFacturar, tarifaDia);
          }

          showToast(`${tarifas.length} tarifa(s) disponible(s)`, "success");
        } else {
          showToast("Esta naviera no tiene tarifas registradas", "error");
        }
      } catch (error) {
        if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
          showToast("Error al cargar tarifas", "error");
        }
      }
    }
  };

  const actualizarCalculos = (valores, tarifaPorDiaOverride = null, aplicaIVAOverride = null, aplicaMargenOverride = null) => {
    const diasDemoraCalculados = valores.diasDemora || 0;
    setDiasDemoras(diasDemoraCalculados);

    // Usar tarifa pasada como parámetro o la del estado
    const tarifaDia = tarifaPorDiaOverride !== null ? tarifaPorDiaOverride : (tarifaPorDia || 0);
    // Usar override de IVA si se pasa, sino leer del formData actual
    const usaIVA = aplicaIVAOverride !== null ? aplicaIVAOverride : (formData.aplicaIVA !== false);
    // Usar override de margen si se pasa, sino leer del formData actual
    const usaMargen = aplicaMargenOverride !== null ? aplicaMargenOverride : (formData.aplicaMargen !== false);

    // Cálculo de costo naviera
    const costoNavieraSinIVA = tarifaDia * diasDemoraCalculados;
    const costoNavieraConIVA = usaIVA ? costoNavieraSinIVA * 1.16 : costoNavieraSinIVA;

    // Cálculo de venta cliente: (tarifa + $15) * días (o sin margen si aplicaMargen=false)
    const margenEfectivo = usaMargen ? 15 : 0;
    const ventaClienteSinIVA = (tarifaDia + margenEfectivo) * diasDemoraCalculados;
    const ventaClienteConIVA = usaIVA ? ventaClienteSinIVA * 1.16 : ventaClienteSinIVA;

    // Utilidad
    const utilidad = ventaClienteSinIVA - costoNavieraSinIVA;

    setFormData((prev) => ({
      ...prev,
      diasIncurridos: diasDemoraCalculados,
      costoNavieraSinIVA: costoNavieraSinIVA ? costoNavieraSinIVA.toFixed(2) : "0.00",
      ventaClienteSinIVA: ventaClienteSinIVA ? ventaClienteSinIVA.toFixed(2) : "0.00",
    }));

    setCalculatedValues({
      costoNavieraConIVA,
      ventaClienteConIVA,
      utilidad,
    });
  };

  const recalcularDesdeFechas = (ataValue, fechaVacioValue, diasLibres) => {
    const fechaAta = parseLocalDate(ataValue);

    if (!fechaAta) {
      setFormData((prev) => ({
        ...prev,
        ultimoDiaSinDemoras: "",
        diasIncurridos: "",
        costoNavieraSinIVA: "",
        ventaClienteSinIVA: "",
      }));
      setDiasDemoras(0);
      setAlertaUrgencia(null);
      setCalculatedValues({
        costoNavieraConIVA: 0,
        ventaClienteConIVA: 0,
        utilidad: 0,
      });
      return;
    }

    const ultimoDia = addDays(fechaAta, diasLibres - 1);
    const ultimoDiaFormatted = formatLocalDate(ultimoDia);
    // Primer día de demoras = último día sin demoras + 1
    const primerDiaDemoras = formatLocalDate(addDays(ultimoDia, 1));

    setFormData((prev) => ({
      ...prev,
      ultimoDiaSinDemoras: ultimoDiaFormatted,
      ultimaFactura: primerDiaDemoras,
    }));

    const estado = calcularEstadoLocal(ultimoDiaFormatted, fechaVacioValue);

    // Para MAERSK usar tarifa efectiva escalonada; para otras navieras flujo normal
    if (esMaersk(formData.naviera) && tarifaMaerskManual !== '') {
      const tarifaEfectiva = calcularTarifaEfectivaMaersk(
        estado.diasDemora,
        maerskTier1Dias,
        tarifaMaerskManual,
        maerskTier2Tarifa
      );
      setTarifaPorDia(tarifaEfectiva);
      actualizarCalculos(estado, tarifaEfectiva);
    } else {
      actualizarCalculos(estado);
    }

    const alerta = construirAlertaUrgencia(
      estado.diasRestantes,
      estado.diasDemora,
      estado.urgencia
    );
    setAlertaUrgencia(alerta);

    // Calcular días a facturar usando el primerDiaDemoras recién calculado
    recalcularFacturacion(primerDiaDemoras, formData.hastaQueFecha, fechaVacioValue);
  };

  const recalcularFacturacion = (ultimaFacturaValue, hastaQueFechaValue, fechaVacioValue) => {
    const fechaInicio = parseLocalDate(ultimaFacturaValue);
    // Si hay hastaQueFecha usa esa; si no, usa fechaVacio; si no, usa hoy
    const endDate = hastaQueFechaValue || fechaVacioValue || null;
    const fechaFinal = parseLocalDate(endDate) || startOfToday();

    if (!fechaInicio || !fechaFinal) {
      setDiasAFacturar(0);
      calcularVentaFacturacion(0, tarifaPorDia);
      return;
    }

    const diffDays = Math.floor(
      (fechaFinal.getTime() - fechaInicio.getTime()) / MS_POR_DIA
    );

    // Sumar 1 para incluir el día inicial en el conteo (cálculo inclusivo)
    // Ejemplo: 01/02 a 16/02 = 15 días diff + 1 = 16 días totales
    const diasCalculados = diffDays >= 0 ? diffDays + 1 : 0;
    setDiasAFacturar(diasCalculados);
    calcularVentaFacturacion(diasCalculados, tarifaPorDia);
  };

  const calcularVentaFacturacion = (diasFacturar, tarifaDia, aplicaIVAOverride = null, aplicaMargenOverride = null) => {
    if (!diasFacturar || diasFacturar <= 0 || !tarifaDia || tarifaDia <= 0) {
      setCalculatedFacturacion({
        ventaFacturacionSinIVA: 0,
        ventaFacturacionConIVA: 0,
      });
      return;
    }

    const usaIVA = aplicaIVAOverride !== null ? aplicaIVAOverride : (formData.aplicaIVA !== false);
    const usaMargen = aplicaMargenOverride !== null ? aplicaMargenOverride : (formData.aplicaMargen !== false);
    // Fórmula: (tarifa + margen) * días facturados
    const margenEfectivo = usaMargen ? 15 : 0;
    const ventaSinIVA = (tarifaDia + margenEfectivo) * diasFacturar;
    const ventaConIVA = usaIVA ? ventaSinIVA * 1.16 : ventaSinIVA;

    setCalculatedFacturacion({
      ventaFacturacionSinIVA: ventaSinIVA,
      ventaFacturacionConIVA: ventaConIVA,
    });
  };

  // Calcula el desglose de tramos Maersk según lo que el usuario haya escrito.
  // Reglas:
  //   - Si tier1Dias está vacío → tarifa plana: todos los días usan tier1Tarifa.
  //   - Si tier2Tarifa está vacío → solo aplica tier 1 (sin segundo tramo).
  //   - Devuelve tarifa efectiva (costoTotal / totalDias) para que el motor existente funcione sin cambios.
  const calcularTarifaEfectivaMaersk = (totalDias, tier1Dias, tier1Tarifa, tier2Tarifa) => {
    const t1Tarifa = parseFloat(tier1Tarifa) || 0;
    const t2Tarifa = parseFloat(tier2Tarifa) || 0;
    const tier1DiasNum = parseInt(tier1Dias);

    // Tier 1 days vacío o 0 → tarifa plana (todos los días en tier 1)
    const diasT1 = (!tier1Dias || isNaN(tier1DiasNum) || tier1DiasNum <= 0)
      ? totalDias
      : Math.min(totalDias, tier1DiasNum);

    // Tier 2 solo aplica si el usuario ingresó una tarifa para él
    const diasT2 = (!tier2Tarifa || t2Tarifa === 0) ? 0 : Math.max(0, totalDias - diasT1);

    const costoTotal = (diasT1 * t1Tarifa) + (diasT2 * t2Tarifa);
    return totalDias > 0 ? costoTotal / totalDias : t1Tarifa;
  };

  const aplicarCalculoMaersk = (tier1Tarifa, tier1Dias, tier2Tarifa, totalDias) => {
    const tarifaEfectiva = calcularTarifaEfectivaMaersk(totalDias, tier1Dias, tier1Tarifa, tier2Tarifa);
    setTarifaPorDia(tarifaEfectiva);
    actualizarCalculos({ diasDemora: totalDias }, tarifaEfectiva);
    if (diasAFacturar > 0) {
      calcularVentaFacturacion(diasAFacturar, tarifaEfectiva);
    }
  };

  const handleTarifaMaerskChange = (valor) => {
    setTarifaMaerskManual(valor);
    aplicarCalculoMaersk(valor, maerskTier1Dias, maerskTier2Tarifa, diasDemoras);
  };

  const handleMaerskTier1DiasChange = (valor) => {
    setMaerskTier1Dias(valor);
    aplicarCalculoMaersk(tarifaMaerskManual, valor, maerskTier2Tarifa, diasDemoras);
  };

  const handleMaerskTier2TarifaChange = (valor) => {
    setMaerskTier2Tarifa(valor);
    aplicarCalculoMaersk(tarifaMaerskManual, maerskTier1Dias, valor, diasDemoras);
  };

  const handleDiasLibresMaerskChange = (valor) => {
    setDiasLibresMaersk(valor);
    const dias = parseInt(valor);
    // Solo recalcular si el usuario escribió un número válido
    if (!isNaN(dias) && dias > 0 && formData.ata) {
      recalcularDesdeFechas(formData.ata, formData.fechaVacio, dias);
    }
  };

  const handleTarifaChange = (tarifaId) => {
    const tarifaSeleccionada = tarifasDisponibles.find(
      (t) => t.id === parseInt(tarifaId)
    );

    if (tarifaSeleccionada) {
      const tarifaDia = parseFloat(tarifaSeleccionada.tarifaSinIVA);

      setFormData((prev) => ({ ...prev, tarifaId: tarifaSeleccionada.id }));
      setTarifaPorDia(tarifaDia);
      setTarifaIncluyeIVA(tarifaSeleccionada.incluyeIVA || false);
      calcularVentaFacturacion(diasAFacturar, tarifaDia);

      if (formData.ata) {
        recalcularDesdeFechas(
          formData.ata,
          formData.fechaVacio,
          obtenerDiasLibresSeleccionados()
        );
      } else {
        // ✅ Usar la función centralizada de cálculos
        const dias = parseFloat(formData.diasIncurridos) || 0;
        actualizarCalculos({ diasDemora: dias }, tarifaDia);
      }

      showToast("Tarifa actualizada", "success");
    }
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import("xlsx");

      // ========== USAR DATOS FILTRADOS ==========
      const dataToExport = filteredRegistros.map((r) => ({
        "Folio": r.folio || "",
        "Contenedor": r.contenedor || "",
        "Cliente": r.cliente || "",
        "Naviera": r.naviera || "",
        "POD": r.pod || "",
        "ATA": r.ata || "",
        "Último Día de Demoras": r.ultimoDiaSinDemoras || "",
        "Fecha de Vacío": r.fechaVacio || "",
        "Días Generados": r.diasDemoras || 0,
        "Estado": r.colorEstado || "",
        "Costo Sin IVA": (r.costoNavieraSinIVA || 0).toFixed(2),
        "Costo Con IVA": (r.costoNavieraConIVA || 0).toFixed(2),
        "Venta Sin IVA": (r.ventaClienteSinIVA || 0).toFixed(2),
        "Venta Con IVA": (r.ventaClienteConIVA || 0).toFixed(2),
        "Utilidad": ((r.ventaClienteConIVA || 0) - (r.costoNavieraConIVA || 0)).toFixed(2),
        "Factura": r.factura || "NO",
        "Factura y Pago a Naviera": r.facturaPagoNaviera || "",
      }));

      // ========== CALCULAR TOTALES ==========
      const totalDias = filteredRegistros.reduce((sum, r) => sum + (r.diasDemoras || 0), 0);
      const totalCostoSinIVA = filteredRegistros.reduce((sum, r) => sum + (r.costoNavieraSinIVA || 0), 0);
      const totalCostoConIVA = filteredRegistros.reduce((sum, r) => sum + (r.costoNavieraConIVA || 0), 0);
      const totalVentaSinIVA = filteredRegistros.reduce((sum, r) => sum + (r.ventaClienteSinIVA || 0), 0);
      const totalVentaConIVA = filteredRegistros.reduce((sum, r) => sum + (r.ventaClienteConIVA || 0), 0);
      const totalUtilidad = totalVentaConIVA - totalCostoConIVA;

      // Agregar fila de totales
      dataToExport.push({
        "Folio": "",
        "Contenedor": "",
        "Cliente": "",
        "Naviera": "",
        "POD": "",
        "ATA": "",
        "Último Día de Demoras": "TOTALES",
        "Fecha de Vacío": "",
        "Días Generados": totalDias,
        "Estado": "",
        "Costo Sin IVA": totalCostoSinIVA.toFixed(2),
        "Costo Con IVA": totalCostoConIVA.toFixed(2),
        "Venta Sin IVA": totalVentaSinIVA.toFixed(2),
        "Venta Con IVA": totalVentaConIVA.toFixed(2),
        "Utilidad": totalUtilidad.toFixed(2),
        "Factura": "",
        "Factura y Pago a Naviera": "",
      });

      // Crear worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);

      // ========== AUTOFILTROS ==========
      const range = XLSX.utils.decode_range(ws['!ref']);
      ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) };

      // ========== ANCHOS DE COLUMNA AUTOMÁTICOS ==========
      const colWidths = Object.keys(dataToExport[0] || {}).map(key => ({
        wch: Math.max(
          key.length,
          ...dataToExport.map(row => (row[key] || '').toString().length)
        ) + 2
      }));
      ws['!cols'] = colWidths;

      // Crear workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contenedores");

      // ========== NOMBRE DE ARCHIVO LIMPIO ==========
      const fecha = new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const nombreArchivo = `Contenedores_${fecha}.xlsx`;

      // Guardar
      XLSX.writeFile(wb, nombreArchivo);
      showToast("Reporte Excel generado", "success");
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      showToast("Error al generar reporte Excel", "error");
    }
  };

  const exportToPDF = async () => {
    try {
      const jsPDF = (await import("jspdf")).default;
      const autoTable = (await import("jspdf-autotable")).default;

      // ========== DOCUMENTO EN HORIZONTAL ==========
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const ROWS_PER_PAGE = 10;

      // ========== CARGAR LOGOTIPO ==========
      let logoBase64 = null;
      try {
        const response = await fetch('/LogoDragon.png');
        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          await new Promise((resolve) => {
            reader.onloadend = () => {
              logoBase64 = reader.result;
              resolve();
            };
            reader.readAsDataURL(blob);
          });
        }
      } catch (error) {
        console.log('No se pudo cargar el logotipo:', error);
      }

      // ========== FUNCIONES AUXILIARES ==========
      const drawHeader = (showFilters) => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, 50, 'F');

        if (logoBase64) {
          try {
            doc.addImage(logoBase64, 'PNG', 8, 4, 35, 15);
          } catch (error) {
            console.log('Error al agregar logo al PDF:', error);
          }
        }

        doc.setTextColor(220, 38, 38);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('REPORTE DE CONTENEDORES', pageWidth / 2, 20, { align: 'center' });

        const fecha = new Date().toLocaleString('es-MX', {
          dateStyle: 'full',
          timeStyle: 'short'
        });
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text(fecha, pageWidth / 2, 27, { align: 'center' });

        doc.setDrawColor(239, 68, 68);
        doc.setLineWidth(0.5);
        doc.line(14, 32, pageWidth - 14, 32);

        let y = 35;

        if (showFilters) {
          if (searchTerm) {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`Filtro aplicado: "${searchTerm}"`, 14, y);
            doc.text(`Mostrando ${filteredRegistros.length} de ${registros.length} contenedores`, 14, y + 5);
            y += 12;
          } else {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`Total de contenedores: ${registros.length}`, 14, y);
            y += 8;
          }
        }

        return y;
      };

      const drawFooter = (pageNum, totalPages) => {
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Página ${pageNum} de ${totalPages}`, 10, pageHeight - 8);
        doc.text(
          `Generado: ${new Date().toLocaleString('es-MX')}`,
          pageWidth - 70,
          pageHeight - 8
        );
      };

      const tableColumns = [
        "Folio", "Contenedor", "Cliente", "Naviera", "POD",
        "ATA", "Último Día", "F. Vacío", "Días", "Costo s/IVA", "Costo c/IVA"
      ];

      const tableStyles = {
        theme: 'striped',
        styles: {
          fontSize: 6.5,
          cellPadding: 1.8,
          overflow: 'linebreak',
          lineWidth: 0.1,
          lineColor: [200, 200, 200],
          valign: 'middle',
        },
        headStyles: {
          fillColor: [239, 68, 68],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          cellPadding: 2,
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 24 },   // Folio
          1: { halign: 'center', cellWidth: 30 },   // Contenedor
          2: { halign: 'left', cellWidth: 30 },     // Cliente
          3: { halign: 'left', cellWidth: 26 },     // Naviera
          4: { halign: 'left', cellWidth: 26 },     // POD
          5: { halign: 'center', cellWidth: 20 },   // ATA
          6: { halign: 'center', cellWidth: 20 },   // Último Día
          7: { halign: 'center', cellWidth: 20 },   // F. Vacío
          8: { halign: 'center', cellWidth: 12 },   // Días
          9: { halign: 'right', cellWidth: 22 },    // Costo s/IVA
          10: { halign: 'right', cellWidth: 22 },   // Costo c/IVA
        },
        margin: { top: 15, right: 10, bottom: 20, left: 10 },
      };

      // ========== PREPARAR DATOS ==========
      const tableData = filteredRegistros.map((r) => [
        r.folio || "",
        r.contenedor || "",
        r.cliente || "",
        r.naviera || "",
        r.pod || "",
        r.ata || "",
        r.ultimoDiaSinDemoras || "",
        r.fechaVacio || "",
        r.diasDemoras || 0,
        `$${(parseFloat(r.costoNavieraSinIVA) || 0).toFixed(2)}`,
        `$${(parseFloat(r.costoNavieraConIVA) || 0).toFixed(2)}`,
      ]);

      // ========== PAGINAR EN BLOQUES DE 10 ==========
      const chunks = [];
      for (let i = 0; i < tableData.length; i += ROWS_PER_PAGE) {
        chunks.push(tableData.slice(i, i + ROWS_PER_PAGE));
      }
      if (chunks.length === 0) chunks.push([]);

      chunks.forEach((chunk, pageIndex) => {
        if (pageIndex > 0) doc.addPage();

        const startY = drawHeader(pageIndex === 0);

        autoTable(doc, {
          head: [tableColumns],
          body: chunk,
          startY: startY,
          ...tableStyles,
        });
      });

      // ========== RESUMEN EJECUTIVO ==========
      const totalCostoSinIVA = filteredRegistros.reduce((sum, r) => sum + (parseFloat(r.costoNavieraSinIVA) || 0), 0);
      const totalCostoConIVA = filteredRegistros.reduce((sum, r) => sum + (parseFloat(r.costoNavieraConIVA) || 0), 0);
      const totalDias = filteredRegistros.reduce((sum, r) => sum + (parseFloat(r.diasDemoras) || 0), 0);

      const resumenWidth = 95;
      const resumenHeight = 26;
      let resumenY = doc.lastAutoTable.finalY + 10;

      // Si no cabe en la página actual, agregar nueva página
      if (resumenY + resumenHeight > pageHeight - 20) {
        doc.addPage();
        drawHeader(false);
        resumenY = 40;
      }

      const resumenX = pageWidth - 10 - resumenWidth;

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.rect(resumenX, resumenY, resumenWidth, resumenHeight);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text("RESUMEN", resumenX + resumenWidth / 2, resumenY + 6, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);

      const labelX = resumenX + 5;
      const valueX = resumenX + resumenWidth - 5;

      doc.text('Total Días:', labelX, resumenY + 12);
      doc.text(`${totalDias}`, valueX, resumenY + 12, { align: 'right' });

      doc.text('Total Costo s/IVA:', labelX, resumenY + 17);
      doc.text(`$${totalCostoSinIVA.toFixed(2)}`, valueX, resumenY + 17, { align: 'right' });

      doc.text('Total Costo c/IVA:', labelX, resumenY + 22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(`$${totalCostoConIVA.toFixed(2)}`, valueX, resumenY + 22, { align: 'right' });

      // ========== FOOTERS EN TODAS LAS PÁGINAS ==========
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        drawFooter(i, totalPages);
      }

      // ========== GUARDAR ==========
      const fechaArchivo = new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const nombreArchivo = `Contenedores_${fechaArchivo}.pdf`;

      doc.save(nombreArchivo);
      showToast("Reporte PDF generado", "success");

    } catch (error) {
      console.error('Error al exportar PDF:', error);
      showToast("Error al generar reporte PDF", "error");
    }
  };

  const openModal = (mode, record = null) => {
    setModalMode(mode);
    if (mode === "edit" && record) {
      setEditingRecord(record);
      setFormData({
        folio: record.folio || "",
        master: record.master || "",
        contenedor: record.contenedor || "",
        cliente: record.cliente || "",
        naviera: record.naviera || "",
        pod: record.pod || "",
        tarifaId: record.tarifaId || "",
        ata: record.ata || "",
        ultimoDiaSinDemoras: record.ultimoDiaSinDemoras || "",
        diasIncurridos: record.diasIncurridos || "",
        fechaVacio: record.fechaVacio || "",
        costoNavieraSinIVA: record.costoNavieraSinIVA || "",
        ventaClienteSinIVA: record.ventaClienteSinIVA || "",
        ultimaFactura: record.ultimaFactura || "",
        factura: record.factura || "NO",
        hastaQueFecha: record.hastaQueFecha || "",
        facturaPagoNaviera: record.facturaPagoNaviera || "",
        pagado: record.pagado || "NO",
        aplicaIVA: record.aplicaIVA !== undefined && record.aplicaIVA !== null ? Boolean(record.aplicaIVA) : true,
        aplicaMargen: record.aplicaMargen !== undefined && record.aplicaMargen !== null ? Boolean(record.aplicaMargen) : true,
      });
      // Recalcular desde las fechas del registro (no depender del valor stale del DB)
      const estadoCalculado = record.ultimoDiaSinDemoras
        ? calcularEstadoLocal(record.ultimoDiaSinDemoras, record.fechaVacio || "")
        : { diasRestantes: null, diasDemora: 0, urgencia: null };

      const diasDemoraCorrecto = estadoCalculado.diasDemora;
      setDiasDemoras(diasDemoraCorrecto);

      // Mostrar alerta correcta desde las fechas (no del modal anterior)
      setAlertaUrgencia(construirAlertaUrgencia(
        estadoCalculado.diasRestantes,
        estadoCalculado.diasDemora,
        estadoCalculado.urgencia
      ));

      // Restaurar modo MAERSK si el registro tiene tarifa_override guardada
      if (esMaersk(record.naviera)) {
        const diasRec = calcularDiasLibresDesdeRegistro(record.ata, record.ultimoDiaSinDemoras);
        setDiasLibresMaersk(diasRec ? String(diasRec) : '');
        // Restaurar exactamente lo que el usuario guardó — sin asumir defaults
        setTarifaMaerskManual(record.tarifa_override != null ? String(parseFloat(record.tarifa_override)) : '');
        setMaerskTier1Dias(record.maersk_tier1_dias != null ? String(record.maersk_tier1_dias) : '');
        setMaerskTier2Tarifa(record.maersk_tier2_tarifa != null ? String(parseFloat(record.maersk_tier2_tarifa)) : '');
        if (record.tarifa_override != null) {
          const tarifaEfectiva = calcularTarifaEfectivaMaersk(
            diasDemoraCorrecto,
            record.maersk_tier1_dias,
            record.tarifa_override,
            record.maersk_tier2_tarifa
          );
          setTarifaPorDia(tarifaEfectiva);
          // Poblar Costos y Ventas con el valor calculado correctamente
          actualizarCalculos(
            { diasDemora: diasDemoraCorrecto },
            tarifaEfectiva,
            record.aplicaIVA !== false,
            record.aplicaMargen !== false
          );
        }
      } else {
        setTarifaMaerskManual('');
        setMaerskTier1Dias('');
        setMaerskTier2Tarifa('');
      }

      // Recalcular días a facturar usando fechaVacio si no hay hastaQueFecha
      recalcularFacturacion(
        record.ultimaFactura || "",
        record.hastaQueFecha || "",
        record.fechaVacio || ""
      );

      // ✅ CARGAR TARIFA Y NAVIERA AL EDITAR — solo para navieras normales (no MAERSK)
      if (record.naviera && !esMaersk(record.naviera)) {
        const navieraSeleccionada = navieras.find((n) => n.nombre === record.naviera);
        if (navieraSeleccionada) {
          apiService.get(`/tarifas/naviera/${navieraSeleccionada.id}`)
            .then(tarifas => {
              setTarifasDisponibles(tarifas);

              if (record.tarifaId) {
                const tarifaUsada = tarifas.find((t) => t.id === record.tarifaId);
                if (tarifaUsada) {
                  const tarifaDia = parseFloat(tarifaUsada.tarifaSinIVA);
                  setTarifaPorDia(tarifaDia);
                  setTarifaIncluyeIVA(tarifaUsada.incluyeIVA || false);
                  actualizarCalculos({ diasDemora: diasDemoraCorrecto }, tarifaDia, record.aplicaIVA !== false, record.aplicaMargen !== false);
                  calcularVentaFacturacion(record.diasAFacturar || 0, tarifaDia);
                  return;
                }
              }

              // Sin tarifaId o no encontrada — derivar tarifa desde costos guardados
              if (record.diasAFacturar > 0 && diasDemoraCorrecto > 0 && record.costoNavieraSinIVA > 0) {
                const tarifaDerivada = parseFloat(record.costoNavieraSinIVA) / diasDemoraCorrecto;
                setTarifaPorDia(tarifaDerivada);
                calcularVentaFacturacion(parseFloat(record.diasAFacturar), tarifaDerivada);
              }
            })
            .catch(err => {
              console.error('Error cargando tarifas:', err);
              if (record.diasAFacturar > 0 && diasDemoraCorrecto > 0 && record.costoNavieraSinIVA > 0) {
                const tarifaCalculada = parseFloat(record.costoNavieraSinIVA) / diasDemoraCorrecto;
                setTarifaPorDia(tarifaCalculada);
                calcularVentaFacturacion(parseFloat(record.diasAFacturar), tarifaCalculada);
              }
            });
        }
      }
    } else {
      setEditingRecord(null);
      resetForm();
      setModoFolio("nuevo");
      setFolioSeleccionado("");
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    const dataToSend = {
      ...formData,
      ata: formData.ata || null,
      ultimoDiaSinDemoras: formData.ultimoDiaSinDemoras || null,
      fechaVacio: formData.fechaVacio || null,
      ultimaFactura: formData.ultimaFactura || null,
      hastaQueFecha: formData.hastaQueFecha || null,
      facturaPagoNaviera: formData.facturaPagoNaviera || null,
      factura: formData.factura || "NO",
      diasDemoras: diasDemoras,
      diasAFacturar: diasAFacturar,
      pagado: formData.pagado,
      ventaFacturacionSinIVA: calculatedFacturacion.ventaFacturacionSinIVA,
      ventaFacturacionConIVA: calculatedFacturacion.ventaFacturacionConIVA,
      // Campos exclusivos de MAERSK
      tarifa_override:       esMaersk(formData.naviera) ? (parseFloat(tarifaMaerskManual) || null) : null,
      maersk_tier1_dias:     esMaersk(formData.naviera) ? (parseInt(maerskTier1Dias) || null) : null,
      maersk_tier2_tarifa:   esMaersk(formData.naviera) ? (parseFloat(maerskTier2Tarifa) || null) : null,
    };

    try {
      const endpoint =
        modalMode === "create"
          ? `/registros`
          : `/registros/${editingRecord.id}`;

      const method = modalMode === "create" ? "post" : "put";

      await apiService[method](endpoint, dataToSend);

      showToast(
        modalMode === "create"
          ? "Registro creado exitosamente"
          : "Registro actualizado exitosamente",
        "success"
      );

      await fetchRegistros();
      await fetchClientes();
      await fetchFolios();

      try {
        if (typeof refrescar === "function") {
          refrescar();
        }
      } catch (err) {
        console.log("No se pudo refrescar notificaciones");
      }

      if (modalMode === "create") {
        resetForm();
        showToast("Formulario limpio. Listo para otro registro.", "success");
      } else {
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al guardar registro", "error");
      }
    }
  };

  const handleDeleteClick = (id) => {
    setRecordToDelete(id);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await apiService.delete(`/registros/${recordToDelete}`);

      if (result.clienteEliminado) {
        showToast(`Registro y cliente "${result.clienteEliminado}" eliminados (sin contenedores restantes)`, "success");
      } else {
        showToast("Registro eliminado exitosamente", "success");
      }
      await fetchRegistros();
      await fetchClientes();

      try {
        if (typeof refrescar === "function") {
          refrescar();
        }
      } catch (err) {
        console.log("No se pudo refrescar notificaciones");
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        showToast("Error al eliminar registro", "error");
      }
    }

    setShowConfirmDialog(false);
    setRecordToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setRecordToDelete(null);
  };

  const abrirTimeline = (contenedor) => {
    setContenedorTimeline(contenedor);
    setMostrarTimeline(true);
  };

  // Funciones para panel lateral
  const abrirPanel = (registro) => {
    console.log('=== Abriendo Panel Lateral ===');
    console.log('Registro completo:', registro);
    console.log('Valores clave:', {
      diasDemoras: registro.diasDemoras,
      diasAFacturar: registro.diasAFacturar,
      costoNavieraSinIVA: registro.costoNavieraSinIVA,
      costoNavieraConIVA: registro.costoNavieraConIVA,
      ventaClienteSinIVA: registro.ventaClienteSinIVA,
      ventaClienteConIVA: registro.ventaClienteConIVA,
      factura: registro.factura,
      tarifaId: registro.tarifaId
    });

    // Enriquecer registro con tarifa calculada si es posible
    const registroEnriquecido = { ...registro };

    // Intentar calcular tarifa por día desde los datos disponibles
    if (registro.diasDemoras > 0 && registro.costoNavieraSinIVA > 0) {
      registroEnriquecido.tarifaPorDiaCalculada = parseFloat(registro.costoNavieraSinIVA) / parseFloat(registro.diasDemoras);
      console.log('Tarifa calculada desde diasDemoras:', registroEnriquecido.tarifaPorDiaCalculada);
    } else if (registro.ventaClienteSinIVA > 0 && registro.diasDemoras > 0) {
      // Si no hay costo pero sí hay venta, derivar tarifa desde venta
      // venta = (tarifa + 15) * dias
      // tarifa = (venta / dias) - 15
      const tarifaDerivada = (parseFloat(registro.ventaClienteSinIVA) / parseFloat(registro.diasDemoras)) - 15;
      if (tarifaDerivada > 0) {
        registroEnriquecido.tarifaPorDiaCalculada = tarifaDerivada;
        console.log('Tarifa derivada desde ventaClienteSinIVA:', registroEnriquecido.tarifaPorDiaCalculada);
      }
    }

    setRegistroSeleccionado(registroEnriquecido);
    setPanelAbierto(true);
  };

  const cerrarPanel = () => {
    setPanelAbierto(false);
    // Delay para animación de salida
    setTimeout(() => setRegistroSeleccionado(null), 300);
  };

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar eliminación"
        message="¿Estás seguro de que deseas eliminar este registro?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="flex items-center justify-between mb-8 animate-fade-in">
        <div>
          <h1 className="font-display text-3xl text-ink-900 mb-1">
            Contenedores
          </h1>
          <p className="text-ink-400">
            Vista global de todos los contenedores
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {/* BOTÓN RECALCULAR COSTOS */}
          <button
            onClick={recalcularCostos}
            disabled={recalculando}
            className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-soft"
            title="Actualizar costos y demoras de todos los registros"
          >
            <RefreshCw size={20} className={recalculando ? 'animate-spin' : ''} />
            <span>{recalculando ? 'Recalculando...' : 'Recalcular Costos'}</span>
          </button>

          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-soft"
          >
            <FileDown size={20} />
            <span>Exportar Excel</span>
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-soft"
          >
            <Download size={20} />
            <span>Exportar PDF</span>
          </button>

          <button
            onClick={abrirModalReporte}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-soft border-2 border-blue-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-semibold">Reporte Personalizado</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft border border-ink-200 p-6 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Buscar en todos los campos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => openModal("create")}
            className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition whitespace-nowrap shadow-soft"
          >
            <Plus size={20} />
            <span>Nuevo Registro</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-max">
            <thead>
              <tr className="bg-ink-50 border-b-2 border-ink-200">
                {[
                  { key: "estadoDemora", label: "Estado Demora", primary: true },
                  { key: "contenedor", label: "Contenedor" },
                  { key: "cliente", label: "Cliente" },
                  { key: "naviera", label: "Naviera" },
                  { key: "diasDemoras", label: "Días de Demoras" },
                  { key: "costoNavieraConIVA", label: "Costo Naviera" },
                  { key: "ventaClienteConIVA", label: "Venta Cliente" },
                ].map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`px-4 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap cursor-pointer select-none hover:bg-ink-100 transition-colors ${col.primary ? "text-ink-900" : "text-gray-700"}`}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {sortColumn === col.key ? (
                        sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} className="text-ink-300" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-semibold text-ink-900 uppercase sticky right-0 bg-ink-50 shadow-lg whitespace-nowrap">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {currentItems.length > 0 ? (
                currentItems.map((registro) => (
                  <tr
                    key={registro.id}
                    className="hover:bg-ink-50 hover:scale-[1.01] hover:shadow-md hover:z-10 transition-all duration-150 cursor-pointer relative"
                    onClick={() => abrirPanel(registro)}
                  >
                    {/* Columna 1: Estado Demora */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      {(() => {
                        const diasRestantes = registro.diasRestantes;
                        const colorEstado = registro.colorEstado;

                        let badge;

                        if (diasRestantes === null || diasRestantes === undefined) {
                          badge = <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">-</span>;
                        } else if (diasRestantes > 5) {
                          badge = (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {diasRestantes}d restantes
                            </span>
                          );
                        } else if (colorEstado === 'verde' || (diasRestantes >= 4 && diasRestantes <= 5)) {
                          badge = (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              {diasRestantes}d restantes
                            </span>
                          );
                        } else if (colorEstado === 'amarillo' || (diasRestantes >= 2 && diasRestantes <= 3)) {
                          badge = (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold animate-pulse">
                              {diasRestantes}d restantes
                            </span>
                          );
                        } else if (diasRestantes >= 0) {
                          badge = (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold animate-pulse">
                              {diasRestantes === 0 ? '¡HOY!' : `${diasRestantes}d restante`}
                            </span>
                          );
                        } else {
                          badge = (
                            <span className="px-2 py-1 bg-red-600 text-white rounded-full text-xs font-bold">
                              DEMORAS
                            </span>
                          );
                        }

                        return badge;
                      })()}
                    </td>
                    {/* Columna 2: Contenedor */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap font-medium">
                      <div className="flex items-center gap-1.5">
                        {registro.estadoNombre?.toLowerCase().includes('devuelt') && ((!registro.diasDemoras || registro.diasDemoras === 0) || registro.pagado !== 'NO') && (
                          <CheckCircle size={15} className={`flex-shrink-0 ${registro.pagado === 'EMPRESA' ? 'text-blue-500' : 'text-emerald-500'}`} title={registro.pagado === 'CLIENTE' ? "Devuelto - Pagado por el cliente" : registro.pagado === 'EMPRESA' ? "Devuelto - Absorbido por Dragon Cargo" : "Devuelto sin demoras"} />
                        )}
                        {registro.contenedor || "-"}
                      </div>
                    </td>
                    {/* Columna 3: Cliente */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      {registro.cliente || "-"}
                    </td>
                    {/* Columna 4: Naviera */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      {registro.naviera || "-"}
                    </td>
                    {/* Columna 5: Días de Demoras */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      {registro.diasDemoras > 0 ? (
                        <span className="px-2 py-1 bg-red-600 text-white rounded-full font-bold">
                          {registro.diasDemoras} días
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">
                          0
                        </span>
                      )}
                    </td>
                    {/* Columna 6: Costo Naviera */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap font-bold text-red-700">
                      ${registro.costoNavieraConIVA?.toFixed(2) || "0.00"}
                      {registro.aplicaIVA === false && (
                        <span className="ml-1 text-[9px] font-semibold text-gray-400 border border-gray-300 rounded px-1 py-0.5 align-middle">s/IVA</span>
                      )}
                    </td>
                    {/* Columna 7: Venta Cliente */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap font-bold text-green-700">
                      ${registro.ventaClienteConIVA?.toFixed(2) || "0.00"}
                      {registro.aplicaIVA === false && (
                        <span className="ml-1 text-[9px] font-semibold text-gray-400 border border-gray-300 rounded px-1 py-0.5 align-middle">s/IVA</span>
                      )}
                      {registro.aplicaMargen === false && (
                        <span className="ml-1 text-[9px] font-semibold text-orange-500 border border-orange-300 rounded px-1 py-0.5 align-middle">s/margen</span>
                      )}
                    </td>
                    {/* Columna 8: Acciones */}
                    <td
                      className="px-4 py-3 text-center sticky right-0 bg-white shadow-lg whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => abrirTimeline(registro)}
                          className="p-2 text-ink-700 hover:bg-ink-100 rounded-lg transition"
                          title="Ver Timeline de Estados"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </button>

                        <button
                          onClick={() => openModal("edit", registro)}
                          className="p-2 text-ink-700 hover:bg-ink-100 rounded-lg transition"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(registro.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-4 py-12 text-center text-ink-500"
                  >
                    <FileSpreadsheet
                      size={48}
                      className="mx-auto text-ink-300 mb-3"
                    />
                    <p className="text-sm font-medium">No hay registros</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-ink-200 flex items-center justify-between">
            <div className="text-sm text-ink-600">
              Mostrando {indexOfFirstItem + 1}-
              {Math.min(indexOfLastItem, sortedRegistros.length)} de{" "}
              {sortedRegistros.length}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 disabled:opacity-50"
              >
                Anterior
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-4 py-2 text-sm rounded-lg ${
                    currentPage === i + 1
                      ? "bg-ink-900 text-white"
                      : "border border-ink-300 text-ink-700 hover:bg-ink-50"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

{/* Modal para preguntar días libres */}
      {showDiasLibresModal && (
        <div className="fixed inset-0 bg-ink-900 bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lifted max-w-md w-full animate-slideIn">
            <div className="px-6 py-5 border-b border-ink-200">
              <h3 className="text-xl font-bold text-ink-900 flex items-center gap-2">
                <Calendar size={24} className="text-ink-700" />
                Cambiar Días Libres
              </h3>
              <p className="text-sm text-ink-600 mt-2">
                ¿Cuántos días libres tiene la naviera <strong>{formData.naviera}</strong> antes de que inicien demoras?
              </p>
            </div>

            <div className="px-6 py-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => confirmarDiasLibres(14)}
                  className="group relative bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6 hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  <div className="text-center">
                    <div className="text-5xl font-bold text-orange-600 mb-2">14</div>
                    <div className="text-sm font-semibold text-orange-900">días libres</div>
                    <div className="text-xs text-orange-700 mt-2">Restrictiva</div>
                  </div>
                  <div className="absolute inset-0 bg-orange-600 opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                <button
                  onClick={() => confirmarDiasLibres(21)}
                  className="group relative bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6 hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  <div className="text-center">
                    <div className="text-5xl font-bold text-green-600 mb-2">21</div>
                    <div className="text-sm font-semibold text-green-900">días libres</div>
                    <div className="text-xs text-green-700 mt-2">Estándar</div>
                  </div>
                  <div className="absolute inset-0 bg-green-600 opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>
              </div>

              <div className="pt-2">
                <p className="text-xs text-ink-600 mb-2 font-semibold">O ingresa un valor personalizado:</p>
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={diasLibresModalValue}
                  onChange={(e) => setDiasLibresModalValue(parseInt(e.target.value, 10) || 21)}
                  className="w-full px-3 py-2 border-2 border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 font-semibold"
                />
              </div>

              <div className="mt-4 bg-ink-50 border border-ink-200 rounded-lg p-3">
                <p className="text-xs text-ink-800">
                  <strong>Nota:</strong> El último día sin demoras se calcula contando {diasLibresModalValue} días desde el ATA (inclusive)
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-ink-200 flex justify-between gap-2">
              <button
                onClick={() => {
                  setShowDiasLibresModal(false);
                }}
                className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => confirmarDiasLibres(diasLibresModalValue)}
                className="px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition text-sm font-medium"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
  <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-40 p-4">
    <div className="bg-white rounded-lg shadow-lifted w-full max-w-5xl max-h-[90vh] overflow-y-auto">
      <div className="px-6 py-4 border-b border-ink-200 flex items-center justify-between sticky top-0 bg-white z-10">
        <h2 className="text-xl font-bold text-ink-900">
          {modalMode === "create" ? "Nuevo Registro" : "Editar Registro"}
        </h2>
        <button
          onClick={() => setShowModal(false)}
          className="text-ink-400 hover:text-ink-600"
        >
          <X size={24} />
        </button>
      </div>

      <div className="px-6 py-4 space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-ink-900 mb-3 flex items-center gap-2">
            <FileSpreadsheet size={16} />
            Datos Generales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Folio
              </label>
              {modalMode === "create" ? (
                // MODO CREACIÓN: Combobox moderno con búsqueda y autocompletado
                <div className="space-y-2">
                  <FolioCombobox
                    folios={foliosDisponibles}
                    value={formData.folio}
                    onChange={handleFolioSelect}
                    onCreateNew={handleCreateNewFolio}
                  />

                  {/* Mensaje informativo si hay folio seleccionado */}
                  {formData.folio && modoFolio !== "nuevo" && (
                    <div className="px-3 py-2 bg-green-50 border border-green-300 rounded-lg text-sm text-green-800">
                      <strong>✅ Folio seleccionado:</strong> {formData.folio}
                      <br />
                      <span className="text-xs">Datos autocompletados. Solo ingresa el nuevo contenedor</span>
                    </div>
                  )}

                  {/* Mensaje informativo si es nuevo folio */}
                  {formData.folio && modoFolio === "nuevo" && (
                    <div className="px-3 py-2 bg-blue-50 border border-blue-300 rounded-lg text-sm text-blue-800">
                      <strong>📝 Nuevo folio:</strong> {formData.folio}
                      <br />
                      <span className="text-xs">Se creará un nuevo contenedor con este folio</span>
                    </div>
                  )}
                </div>
              ) : (
                // MODO EDICIÓN: Input normal (no modificable)
                <input
                  type="text"
                  value={formData.folio}
                  onChange={(e) =>
                    setFormData({ ...formData, folio: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 focus:outline-none"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Master
              </label>
              <input
                type="text"
                value={formData.master}
                onChange={(e) =>
                  setFormData({ ...formData, master: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contenedor *
              </label>
              <input
                type="text"
                value={formData.contenedor}
                onChange={(e) =>
                  setFormData({ ...formData, contenedor: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cliente *
              </label>
              <select
                value={formData.cliente}
                onChange={(e) =>
                  setFormData({ ...formData, cliente: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none bg-white"
                required
              >
                <option value="">Selecciona un cliente</option>
                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.nombre}>
                    {cliente.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naviera *
              </label>
              <div className="relative">
                <Ship
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <select
                  value={formData.naviera}
                  onChange={(e) => handleNavieraChange(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none bg-white"
                  required
                >
                  <option value="">Selecciona una naviera</option>
                  {navieras.map((naviera) => (
                    <option key={naviera.id} value={naviera.nombre}>
                      {naviera.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                POD (Puerto de descarga)
              </label>
              <div className="relative">
                <Anchor
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <select
                  value={formData.pod}
                  onChange={(e) =>
                    setFormData({ ...formData, pod: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none bg-white"
                >
                  <option value="">Selecciona un puerto</option>
                  {puertos.map((puerto) => (
                    <option key={puerto.id} value={puerto.nombre}>
                      {puerto.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {esMaersk(formData.naviera) ? (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5">
            <h3 className="text-sm font-bold text-blue-900 mb-1 flex items-center gap-2">
              <Ship size={15} />
              MAERSK — Tarifa Manual
            </h3>
            <p className="text-xs text-blue-600 mb-4">
              Ingresa los valores exactos que muestra la plataforma de MAERSK. Si no hay segundo tramo, deja Tarifa TIER 2 en blanco.
            </p>

            {/* Días libres */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-blue-800 mb-1">
                Días libres de demoras <span className="text-blue-400 font-normal">(según MAERSK)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={diasLibresMaersk}
                onChange={(e) => handleDiasLibresMaerskChange(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-40 px-3 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm bg-white"
                placeholder="ej. 14"
              />
            </div>

            {/* Tabla de tramos */}
            <div className="rounded-lg overflow-hidden border border-blue-200">
              {/* Encabezado */}
              <div className="grid grid-cols-4 bg-blue-200 text-blue-900 text-xs font-bold px-3 py-2 gap-2">
                <span>Tramo</span>
                <span>Días</span>
                <span>Tarifa / día</span>
                <span className="text-right">Subtotal</span>
              </div>

              {/* Tier 1 */}
              {(() => {
                const tier1DiasNum = parseInt(maerskTier1Dias);
                const tier1TarifaNum = parseFloat(tarifaMaerskManual) || 0;
                // Si tier1Dias vacío → tarifa plana (todos los días en tier 1)
                const diasT1 = (!maerskTier1Dias || isNaN(tier1DiasNum) || tier1DiasNum <= 0)
                  ? diasDemoras
                  : Math.min(diasDemoras, tier1DiasNum);
                const t1Sub = diasT1 * tier1TarifaNum;
                return (
                  <div className="grid grid-cols-4 items-center bg-white border-b border-blue-100 px-3 py-2 gap-2">
                    <span className="text-xs font-semibold text-blue-700">TIER 1</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">Días:</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={maerskTier1Dias}
                        onChange={(e) => handleMaerskTier1DiasChange(e.target.value.replace(/[^0-9]/g, ''))}
                        className="w-14 px-2 py-1 border border-blue-300 rounded text-xs text-center bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="ej. 7"
                      />
                    </div>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400 text-xs font-bold">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={tarifaMaerskManual}
                        onChange={(e) => handleTarifaMaerskChange(e.target.value.replace(/[^0-9.]/g, ''))}
                        className="w-full pl-5 pr-2 py-1 border border-blue-300 rounded text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="ej. 190"
                      />
                    </div>
                    <span className="text-xs font-semibold text-right text-gray-700">
                      ${t1Sub.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })()}

              {/* Tier 2 — solo muestra días calculados si tier 2 tiene tarifa ingresada */}
              {(() => {
                const tier1DiasNum = parseInt(maerskTier1Dias);
                const diasT1 = (!maerskTier1Dias || isNaN(tier1DiasNum) || tier1DiasNum <= 0)
                  ? diasDemoras
                  : Math.min(diasDemoras, tier1DiasNum);
                const diasT2 = Math.max(0, diasDemoras - diasT1);
                const t2Tarifa = parseFloat(maerskTier2Tarifa) || 0;
                const t2Sub = diasT2 * t2Tarifa;
                return (
                  <div className="grid grid-cols-4 items-center bg-blue-50 px-3 py-2 gap-2">
                    <span className="text-xs font-semibold text-blue-700">TIER 2</span>
                    <span className="text-xs text-gray-500">
                      {maerskTier1Dias ? `${diasT2} días restantes` : '—'}
                    </span>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400 text-xs font-bold">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={maerskTier2Tarifa}
                        onChange={(e) => handleMaerskTier2TarifaChange(e.target.value.replace(/[^0-9.]/g, ''))}
                        className="w-full pl-5 pr-2 py-1 border border-blue-300 rounded text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="ej. 200 (opcional)"
                      />
                    </div>
                    <span className="text-xs font-semibold text-right text-gray-700">
                      {maerskTier2Tarifa ? `$${t2Sub.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—'}
                    </span>
                  </div>
                );
              })()}

              {/* Total */}
              {(() => {
                const tier1DiasNum = parseInt(maerskTier1Dias);
                const diasT1 = (!maerskTier1Dias || isNaN(tier1DiasNum) || tier1DiasNum <= 0)
                  ? diasDemoras
                  : Math.min(diasDemoras, tier1DiasNum);
                const diasT2 = maerskTier2Tarifa ? Math.max(0, diasDemoras - diasT1) : 0;
                const total  = (diasT1 * (parseFloat(tarifaMaerskManual) || 0))
                             + (diasT2 * (parseFloat(maerskTier2Tarifa) || 0));
                return (
                  <div className="grid grid-cols-4 items-center bg-blue-100 px-3 py-2 gap-2 border-t border-blue-200">
                    <span className="col-span-3 text-xs font-bold text-blue-900">
                      Total MAERSK{diasDemoras > 0 ? ` · ${diasDemoras} día${diasDemoras !== 1 ? 's' : ''} de demora` : ''}
                    </span>
                    <span className="text-sm font-bold text-right text-blue-900">
                      ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : tarifasDisponibles.length > 0 && (
          <div className="bg-ink-50 border-2 border-ink-300 rounded-xl p-5">
            <h3 className="text-sm font-bold text-ink-900 mb-3">
              Tarifa de Demoras
            </h3>
            <select
              value={formData.tarifaId || ""}
              onChange={(e) => handleTarifaChange(e.target.value)}
              className="w-full px-4 py-3 border-2 border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 bg-white"
              required
            >
              <option value="">Selecciona una tarifa</option>
              {tarifasDisponibles.map((tarifa) => (
                <option key={tarifa.id} value={tarifa.id}>
                  {tarifa.descripcion} - $
                  {parseFloat(tarifa.tarifaSinIVA).toFixed(2)}/día
                  {tarifa.esDefault ? " ⭐" : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold text-ink-900 mb-3 flex items-center gap-2">
            <Calendar size={16} />
            Fechas
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ATA 
              </label>
              <input
                type="date"
                value={formData.ata}
                onChange={(e) => handleAtaChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    📅 Último Día Sin Demoras
                    {formData.ata && !esMaersk(formData.naviera) && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-bold">
                        {obtenerDiasLibresSeleccionados()} días desde ATA
                      </span>
                    )}
                    {formData.ata && esMaersk(formData.naviera) && (
                      <span className="text-xs bg-blue-200 text-blue-900 px-2 py-1 rounded-full font-bold">
                        {diasLibresMaersk} días desde ATA
                      </span>
                    )}
                  </span>
                  {formData.ata && formData.ultimoDiaSinDemoras && !esMaersk(formData.naviera) && (
                    <button
                      type="button"
                      onClick={() => {
                        setDiasLibresModalValue(obtenerDiasLibresSeleccionados());
                        setShowDiasLibresModal(true);
                      }}
                      className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded hover:bg-amber-100 font-semibold transition"
                    >
                      Cambiar días
                    </button>
                  )}
                </span>
              </label>
              <input
                type="date"
                value={formData.ultimoDiaSinDemoras}
                onChange={(e) => {
                  const newUltimoDia = e.target.value;
                  setFormData((prev) => ({ ...prev, ultimoDiaSinDemoras: newUltimoDia }));
                  if (newUltimoDia) {
                    const estado = calcularEstadoLocal(newUltimoDia, formData.fechaVacio);
                    if (esMaersk(formData.naviera) && tarifaMaerskManual !== '') {
                      const tarifaEfectiva = calcularTarifaEfectivaMaersk(
                        estado.diasDemora, maerskTier1Dias, tarifaMaerskManual, maerskTier2Tarifa
                      );
                      setTarifaPorDia(tarifaEfectiva);
                      actualizarCalculos(estado, tarifaEfectiva);
                    } else {
                      actualizarCalculos(estado);
                    }
                    const alerta = construirAlertaUrgencia(
                      estado.diasRestantes,
                      estado.diasDemora,
                      estado.urgencia
                    );
                    setAlertaUrgencia(alerta);
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg font-semibold transition ${
                  formData.ultimoDiaSinDemoras
                    ? 'bg-blue-50 border-blue-300 text-blue-900 focus:ring-2 focus:ring-blue-500'
                    : 'bg-gray-50 border-gray-300 text-gray-600'
                }`}
                placeholder="Se calcula automáticamente (ATA + días libres)"
              />
              <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                {formData.ata && formData.ultimoDiaSinDemoras ? (
                  <span className="text-green-600 font-semibold">✓ Calculado automáticamente. Editable si lo necesitas.</span>
                ) : formData.ata && formData.naviera ? (
                  <span className="text-orange-600 font-semibold">⚠️ Naviera sin días libres configurados. Ingresa manualmente.</span>
                ) : formData.ata ? (
                  <span className="text-orange-600 font-semibold">⚠️ Selecciona una naviera primero</span>
                ) : (
                  <span className="text-gray-500">1️⃣ Naviera → 2️⃣ ATA → Se calcula automáticamente</span>
                )}
              </p>

              {alertaUrgencia && (
                <div
                  className="mt-3 px-3 py-2 rounded-lg font-bold text-sm flex items-center gap-2"
                  style={{
                    backgroundColor: alertaUrgencia.bgColor,
                    color: alertaUrgencia.textColor
                  }}
                >
                  {(alertaUrgencia.nivel === 'critica' || alertaUrgencia.nivel === 'alta') && <AlertTriangle size={16} />}
                  <span>{alertaUrgencia.mensaje}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Vacío
              </label>
              <input
                type="date"
                value={formData.fechaVacio}
                onChange={(e) => {
                  const fechaVacioValue = e.target.value;
                  setFormData((prev) => ({ ...prev, fechaVacio: fechaVacioValue }));
                  // Para MAERSK usar los días libres que ingresó el usuario, no el default de la naviera
                  const diasLibres = esMaersk(formData.naviera)
                    ? parseInt(diasLibresMaersk) || null
                    : obtenerDiasLibresSeleccionados();
                  if (!esMaersk(formData.naviera) || (diasLibres && formData.ata)) {
                    recalcularDesdeFechas(formData.ata, fechaVacioValue, diasLibres);
                  }
                  // Recalcular días a facturar usando fechaVacio como fecha final si no hay hastaQueFecha
                  recalcularFacturacion(formData.ultimaFactura, formData.hastaQueFecha, fechaVacioValue);
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {formData.fechaVacio && (
                <p className="text-xs text-green-600 mt-1 font-semibold flex items-center gap-1">
                  <Check size={14} />
                  Las demoras se congelaron en esta fecha
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-ink-50 border-2 border-ink-300 rounded-xl p-5">
          <h3 className="text-sm font-bold text-ink-900 mb-4 flex items-center gap-2">
            <DollarSign size={16} />
            Información de Facturación
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Última Factura
              </label>
              <input
                type="date"
                value={formData.ultimaFactura}
                onChange={(e) => {
                  const ultimaFacturaValue = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    ultimaFactura: ultimaFacturaValue,
                  }));
                  recalcularFacturacion(ultimaFacturaValue, formData.hastaQueFecha, formData.fechaVacio);
                }}
                className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
              {diasAFacturar > 0 && (
                <p className="text-xs text-blue-600 mt-1 font-semibold">
                  {diasAFacturar} días a facturar
                  {formData.hastaQueFecha && formData.hastaQueFecha.trim() !== '' ? (
                    <span className="text-green-600"> (hasta {formData.hastaQueFecha})</span>
                  ) : formData.fechaVacio && formData.fechaVacio.trim() !== '' ? (
                    <span className="text-amber-600"> (hasta fecha de vacío: {formData.fechaVacio})</span>
                  ) : null}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Días a Facturar
              </label>
              <input
                type="number"
                value={diasAFacturar}
                readOnly
                className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg bg-purple-50 font-semibold"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ¿Demoras Parciales?
              </label>
              <select
                value={formData.factura}
                onChange={(e) => {
                  const nuevoValor = e.target.value;
                  
                  if (nuevoValor === "NO") {
                    setFormData({ 
                      ...formData, 
                      factura: nuevoValor,
                      hastaQueFecha: ''
                    });
                    recalcularFacturacion(formData.ultimaFactura, '', formData.fechaVacio);
                  } else {
                    setFormData({ ...formData, factura: nuevoValor });
                  }
                }}
                className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="NO">NO</option>
                <option value="SI">SÍ</option>
              </select>
            </div>

            {formData.factura === "SI" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hasta qué fecha
                </label>
                <input
                  type="date"
                  value={formData.hastaQueFecha}
                  onChange={(e) => {
                    const hastaQueFechaValue = e.target.value;
                    setFormData({
                      ...formData,
                      hastaQueFecha: hastaQueFechaValue,
                    });
                    recalcularFacturacion(formData.ultimaFactura, hastaQueFechaValue);
                  }}
                  className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-green-50"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Factura y Pago a Naviera
              </label>
              <textarea
                value={formData.facturaPagoNaviera}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    facturaPagoNaviera: e.target.value,
                  })
                }
                rows="3"
                className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Días de Demoras
              </label>
              <input
                type="number"
                value={diasDemoras}
                readOnly
                className={`w-full px-3 py-2 border-2 rounded-lg font-semibold ${
                  diasDemoras > 0
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
              />
            </div>

            {diasDemoras > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ¿Pagado?
                </label>
                <select
                  value={formData.pagado}
                  onChange={(e) =>
                    setFormData({ ...formData, pagado: e.target.value })
                  }
                  className={`w-full px-3 py-2 border-2 rounded-lg font-semibold ${
                    formData.pagado === "CLIENTE"
                      ? 'bg-green-50 border-green-300 text-green-700'
                      : formData.pagado === "EMPRESA"
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-yellow-50 border-yellow-300 text-yellow-700'
                  }`}
                >
                  <option value="NO">PENDIENTE</option>
                  <option value="CLIENTE">PAGADO POR EL CLIENTE</option>
                  <option value="EMPRESA">ABSORBIDO POR DRAGON CARGO</option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="border-2 border-ink-200 rounded-xl p-5 bg-ink-50">
          <h3 className="text-sm font-semibold text-ink-900 mb-4">
            Costos y Ventas (Calculados Automáticamente)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Costo Naviera SIN IVA
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 font-semibold text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.costoNavieraSinIVA}
                  readOnly
                  className="w-full pl-8 pr-3 py-2 border-2 border-blue-200 rounded-lg bg-blue-50 text-gray-700 font-bold cursor-not-allowed"
                />
              </div>
              {diasDemoras > 0 && tarifaPorDia > 0 && (
                <p className="text-xs text-blue-600 mt-1 font-semibold">
                  ${tarifaPorDia}/día × {diasDemoras} días = ${formData.costoNavieraSinIVA}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Venta Cliente SIN IVA
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 font-semibold text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.ventaClienteSinIVA}
                  readOnly
                  className="w-full pl-8 pr-3 py-2 border-2 border-green-200 rounded-lg bg-green-50 text-gray-700 font-bold cursor-not-allowed"
                />
              </div>
              {formData.costoNavieraSinIVA > 0 && (
                <p className="text-xs text-green-600 mt-1 font-semibold">
                  (${tarifaPorDia} + ${formData.aplicaMargen ? '15' : '0'}) × {diasDemoras} días = ${formData.ventaClienteSinIVA}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Toggles IVA y Margen */}
        <div className="flex flex-col gap-2 px-1 py-1">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => {
                const nuevoValor = !formData.aplicaIVA;
                const diasActuales = diasDemoras || parseFloat(formData.diasIncurridos) || 0;
                const tarifaActual = tarifaPorDia > 0 ? tarifaPorDia : 0;
                setFormData(prev => ({ ...prev, aplicaIVA: nuevoValor }));
                if (diasActuales > 0 && tarifaActual > 0) {
                  actualizarCalculos({ diasDemora: diasActuales }, tarifaActual, nuevoValor);
                }
                if (diasAFacturar > 0 && tarifaActual > 0) {
                  calcularVentaFacturacion(diasAFacturar, tarifaActual, nuevoValor);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                formData.aplicaIVA ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                formData.aplicaIVA ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <span className="text-sm font-semibold text-gray-700">Aplicar IVA 16%</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              formData.aplicaIVA ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {formData.aplicaIVA ? 'CON IVA' : 'SIN IVA'}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => {
                const nuevoValor = !formData.aplicaMargen;
                const diasActuales = diasDemoras || parseFloat(formData.diasIncurridos) || 0;
                const tarifaActual = tarifaPorDia > 0 ? tarifaPorDia : 0;
                setFormData(prev => ({ ...prev, aplicaMargen: nuevoValor }));
                if (diasActuales > 0 && tarifaActual > 0) {
                  actualizarCalculos({ diasDemora: diasActuales }, tarifaActual, null, nuevoValor);
                }
                if (diasAFacturar > 0 && tarifaActual > 0) {
                  calcularVentaFacturacion(diasAFacturar, tarifaActual, null, nuevoValor);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                formData.aplicaMargen ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                formData.aplicaMargen ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <span className="text-sm font-semibold text-gray-700">Aplicar margen $15</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              formData.aplicaMargen ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {formData.aplicaMargen ? 'CON MARGEN' : 'SIN MARGEN'}
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="text-green-600" size={20} />
            <h3 className="text-sm font-bold text-green-900">
              Cálculos Finales {formData.aplicaIVA ? '(IVA 16%)' : '(Sin IVA)'}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
              <p className="text-xs text-gray-600 mb-1 font-medium">
                Costo Naviera {formData.aplicaIVA ? 'c/IVA' : 's/IVA'}
              </p>
              <p className="text-2xl font-bold text-red-600">
                ${calculatedValues.costoNavieraConIVA.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
              <p className="text-xs text-gray-600 mb-1 font-medium">
                Venta Cliente {formData.aplicaIVA ? 'c/IVA' : 's/IVA'}
              </p>
              <p className="text-2xl font-bold text-green-600">
                ${calculatedValues.ventaClienteConIVA.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
              <p className="text-xs text-gray-600 mb-1 font-medium">
                Utilidad Total
              </p>
              <p
                className={`text-2xl font-bold ${
                  calculatedValues.utilidad >= 0
                    ? "text-emerald-600"
                    : "text-red-600"
                }`}
              >
                ${calculatedValues.utilidad.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* ========== NUEVA SECCIÓN: VENTA POR DÍAS FACTURADOS ========== */}
        {diasAFacturar > 0 && formData.factura === "SI" && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-5 mt-5">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="text-purple-600" size={20} />
              <h3 className="text-sm font-bold text-purple-900">
                Venta por Días Facturados
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                <p className="text-xs text-gray-600 mb-1 font-medium">
                  Venta Cliente SIN IVA
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  ${calculatedFacturacion.ventaFacturacionSinIVA.toFixed(2)}
                </p>
                <p className="text-xs text-purple-600 mt-2">
                  (${tarifaPorDia} + ${formData.aplicaMargen ? '15' : '0'}) × {diasAFacturar} días
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                <p className="text-xs text-gray-600 mb-1 font-medium">
                  Venta Cliente CON IVA
                </p>
                <p className="text-2xl font-bold text-purple-700">
                  ${calculatedFacturacion.ventaFacturacionConIVA.toFixed(2)}
                </p>
                <p className="text-xs text-purple-600 mt-2">
                  ${calculatedFacturacion.ventaFacturacionSinIVA.toFixed(2)} × 1.16
                </p>
              </div>
            </div>

            <div className="bg-purple-100 rounded-lg p-3 border border-purple-300">
              <p className="text-xs text-purple-800 font-semibold">
                💡 Esta venta está basada en <span className="text-purple-900">{diasAFacturar} días facturados</span> desde {formData.ultimaFactura} hasta {formData.hastaQueFecha || 'hoy'}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
        <button
          onClick={() => setShowModal(false)}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Check size={20} />
          <span>Guardar</span>
        </button>
      </div>
    </div>
  </div>
)}

      <ModalReporteCliente
        isOpen={modalReporteAbierto}
        onClose={() => setModalReporteAbierto(false)}
        clientes={clientes}
        contenedores={registros}
      />
      
      {mostrarTimeline && contenedorTimeline && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Timeline del Contenedor
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-semibold">{contenedorTimeline.contenedor}</span>
                  {' '} - {contenedorTimeline.cliente}
                  {contenedorTimeline.naviera && (
                    <> • <span className="text-blue-600">{contenedorTimeline.naviera}</span></>
                  )}
                </p>
              </div>
              
              <button
                onClick={() => {
                  setMostrarTimeline(false);
                  setContenedorTimeline(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Cerrar"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <TimelineEstados 
                contenedor={contenedorTimeline}
                onEstadoCambiado={(contenedorActualizado) => {
                  fetchRegistros();
                  setContenedorTimeline(contenedorActualizado);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Panel Lateral de Detalles */}
      {panelAbierto && (
        <ContenedorDetailPanel
          isOpen={panelAbierto}
          contenedor={registroSeleccionado}
          onClose={cerrarPanel}
          onEdit={(registro) => {
            cerrarPanel();
            openModal("edit", registro);
          }}
          onTimeline={(registro) => {
            cerrarPanel();
            abrirTimeline(registro);
          }}
          puertos={puertos}
        />
      )}

      {/* Modal de Confirmación: Folio Existente */}
      <ConfirmModal
        isOpen={folioConfirmModal.isOpen}
        onClose={handleCancelarModalFolio}
        onConfirm={handleConfirmarCargaDatos}
        title="Folio con Contenedores Registrados"
        message={`El folio "${folioConfirmModal.folioValue}" ya tiene contenedores registrados.

¿Deseas cargar los datos del último contenedor para agilizar la captura?

• "Cargar Datos Anteriores": Se autocompletarán todos los campos (master, naviera, cliente, tarifas, fechas, etc.). Solo deberás ingresar el nuevo número de contenedor.

• "Comenzar Limpio": Se mantendrá únicamente el número de folio, los demás campos quedarán vacíos para ingreso manual.`}
        confirmText="Cargar Datos Anteriores"
        cancelText="Comenzar Limpio"
        type="info"
      />

      {/* Estilos para animaciones */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-slideIn {
          animation: slideIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Contenedores;
